"use client"
import Link from "next/link"
import Image from "next/image"
import { useState, useEffect, Suspense } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { safeNextPath } from "@/lib/safe-redirect"

export default function LoginPage() {
  return <Suspense><LoginPageInner /></Suspense>
}

function LoginPageInner() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [mode, setMode] = useState<"password" | "phone">("password")
  const [step, setStep] = useState<"credentials" | "otp">("credentials")
  const [sentTo, setSentTo] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [phone, setPhone] = useState("")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [devOtp, setDevOtp] = useState("")
  const [pending, setPending] = useState<any>(null)
  const [resetSuccess, setResetSuccess] = useState(false)

  // Honour ?next= on every successful login. The OAuth paths already did, but the
  // email/password and OTP completions hard-pushed /dashboard — so the gold
  // "List Property" CTA (Navbar.tsx:170 → /auth/login?next=/list-property), the
  // site's primary seller CTA, never delivered anyone to the listing form.
  // safeNextPath rejects absolute/external targets, so this cannot become an
  // open redirect.
  function destination(): string {
    if (typeof window === "undefined") return "/dashboard"
    const next = safeNextPath(new URLSearchParams(window.location.search).get("next"))
    return next && next !== "/" ? next : "/dashboard"
  }

  useEffect(() => {
    const stored = localStorage.getItem("ze_user")
    const expiry = localStorage.getItem("ze_session_expiry")
    if (stored && expiry && new Date(expiry) > new Date()) router.push(destination())
    if (window.location.search.includes("reset=1")) setResetSuccess(true)
    const errorParam = new URLSearchParams(window.location.search).get("error")
    if (errorParam === "oauth_cancelled") setError("Sign-in was cancelled. Please try again.")
    else if (errorParam === "oauth_failed") setError("Sign-in failed. Please try another method.")
    else if (errorParam === "oauth_no_email") setError("Could not get your email from the provider. Try signing in with email and password.")
    else if (errorParam === "email_in_use") setError("An account already uses this email address. Sign in with your password or phone number instead, or reset your password below.")
    else if (errorParam === "email_unverified") setError("Your provider has not verified this email address, so we cannot use it to sign you in. Verify it with them, or sign in with your password or phone number.")
    else if (errorParam === "registrations_closed") setError("New registrations are currently closed. Please check back soon.")
    else if (errorParam === "oauth_invalid_state") setError("Sign-in session expired. Please try again.")
  }, [])

  function setSession(user: any) {
    localStorage.setItem("ze_user", JSON.stringify(user))
    localStorage.setItem("ze_session_expiry", new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString())
    window.dispatchEvent(new Event("ze:auth"))
  }

  function need7DayOTP(): boolean {
    const last = localStorage.getItem("ze_otp_last")
    if (!last) return true
    return Date.now() - parseInt(last) > 7 * 24 * 60 * 60 * 1000
  }

  async function requestOTP(ph: string) {
    const res = await fetch("/api/auth/otp", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: ph }) })
    const data = await res.json()
    if (!data.success) throw new Error(data.error ?? "Failed to send code")
    if (data.devOtp) setDevOtp(data.devOtp)
    setSentTo(data.channel === "email" ? (data.sentTo ?? "") : ph)
  }

  async function handlePasswordLogin() {
    if (!email || !password) { setError("Enter your email and password"); return }
    setLoading(true); setError("")
    try {
      const res = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) })
      const data = await res.json()
      if (!data.success) { setError(data.error ?? "Invalid email or password"); setLoading(false); return }
      const user = data.data
      if (!user.phone) {
        // Account has no phone yet, collect and verify before entering the app
        setSession(user)
        const next = safeNextPath(new URLSearchParams(window.location.search).get("next"))
        router.push("/auth/oauth-phone?next=" + encodeURIComponent(next))
      } else if (need7DayOTP()) {
        await requestOTP(user.phone)
        setPending(user)
        setStep("otp")
      } else {
        setSession(user)
        router.push(destination())
      }
    } catch (e: any) { setError(e.message ?? "Something went wrong") }
    setLoading(false)
  }

  async function handleSendPhoneOTP() {
    if (!phone) { setError("Enter your phone number"); return }
    setLoading(true); setError("")
    try {
      await requestOTP(phone)
      setStep("otp")
    } catch (e: any) { setError(e.message ?? "Something went wrong") }
    setLoading(false)
  }

  async function handleVerifyOTP() {
    if (otp.length !== 6) { setError("Enter the 6-digit code"); return }
    setLoading(true); setError("")
    try {
      const ph = mode === "password" ? pending?.phone : phone
      const res = await fetch("/api/auth/verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: ph, otp }) })
      const data = await res.json()
      if (!data.success) { setError(data.error ?? "Incorrect code"); setLoading(false); return }
      setSession(data.user)
      localStorage.setItem("ze_otp_last", String(Date.now()))
      if (mode === "password") {
        router.push(destination())
      } else {
        if (data.isNew) { router.push("/auth/register"); return }
        router.push(destination())
      }
    } catch { setError("Something went wrong") }
    setLoading(false)
  }

  async function resendOTP() {
    setLoading(true)
    try { await requestOTP(mode === "password" ? pending?.phone : phone); setOtp(""); setError("") } catch (e: any) { setError(e.message) }
    setLoading(false)
  }

  function switchMode(m: "password" | "phone") {
    setMode(m); setStep("credentials"); setError(""); setOtp(""); setDevOtp(""); setPending(null)
  }

  const isOtpStep = step === "otp"
  const otpTarget = sentTo || (mode === "password" ? (pending?.phone ?? "your phone") : `+263${phone}`)

  // From useSearchParams, not window.location: the server has no window, so the
  // href differed from the server HTML and React 19 keeps the server's attribute,
  // which silently dropped ?next= from the Google button.
  const nextParam = searchParams.get("next") ?? ""
  const oauthNext = nextParam ? "?next=" + encodeURIComponent(nextParam) : ""

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Link href="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 32, fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 600, color: "var(--navy)", textDecoration: "none" }}>
            <Image src="/icon-full-color.svg" width={36} height={36} alt="" style={{ background: "var(--cream-warm)", borderRadius: 8, padding: 4, flexShrink: 0, display: "block" }} />
            Zim <span style={{ color: "var(--gold)" }}>Estates</span>
          </Link>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>
            {isOtpStep ? "Verify it's you" : "Log in"}
          </h1>
          <p style={{ color: "var(--muted)", fontSize: 15 }}>
            {isOtpStep ? `We sent a 6-digit code to ${otpTarget}.` : "Log in to your Zim Estates account."}
          </p>
        </div>

        {resetSuccess && (
          <div style={{ background: "rgba(61,139,95,0.1)", border: "1px solid rgba(61,139,95,0.3)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 14, color: "var(--green-text)", fontWeight: 600, textAlign: "center" }}>
            Password reset successfully. Sign in below.
          </div>
        )}

        <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 20, padding: 32, boxShadow: "var(--shadow-lg)" }}>
          {!isOtpStep && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
                <a href={"/api/auth/oauth/google" + oauthNext}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", padding: "12px 16px", border: "1px solid var(--line)", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "var(--navy)", background: "var(--cream)", textDecoration: "none", cursor: "pointer", fontFamily: "inherit" }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Continue with Google
                </a>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
                <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500 }}>or sign in with</span>
                <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
              </div>
            </>
          )}
          {!isOtpStep && (
            <div style={{ display: "flex", marginBottom: 28, border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
              {(["password", "phone"] as const).map(m => (
                <button key={m} onClick={() => switchMode(m)} style={{ flex: 1, padding: "10px", background: mode === m ? "var(--navy)" : "var(--cream)", color: mode === m ? "var(--cream)" : "var(--muted)", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  {m === "password" ? "Email + Password" : "Phone OTP"}
                </button>
              ))}
            </div>
          )}

          {mode === "password" && !isOtpStep && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="login-email" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Email address</label>
                <input id="login-email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handlePasswordLogin()} placeholder="you@email.com" type="email" autoComplete="email" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label htmlFor="login-password" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Password</label>
                <input id="login-password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handlePasswordLogin()} placeholder="Your password" type="password" autoComplete="current-password" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
              </div>
              <div style={{ textAlign: "right", marginBottom: 20 }}>
                <Link href="/auth/forgot-password" style={{ fontSize: 12, color: "var(--muted)", textDecoration: "underline" }}>Forgot password?</Link>
              </div>
              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8, marginBottom: 16 }}>{error}</div>}
              <button onClick={handlePasswordLogin} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {loading ? "Logging in..." : "Log in"}
              </button>
              <div style={{ marginTop: 16, padding: "12px 14px", background: "var(--cream-warm)", borderRadius: 8, fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>
                Your account is re-verified with an emailed code every 7 days.
              </div>
            </>
          )}

          {mode === "phone" && !isOtpStep && (
            <>
              <div style={{ marginBottom: 20 }}>
                <label htmlFor="login-phone" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Zimbabwe phone number</label>
                <div style={{ display: "flex" }}>
                  <div style={{ padding: "12px 14px", border: "1px solid var(--line)", borderRight: "none", borderRadius: "8px 0 0 8px", fontSize: 14, color: "var(--navy)", fontWeight: 600, background: "var(--cream-warm)", display: "flex", alignItems: "center" }}>+263</div>
                  <input id="login-phone" value={phone} onChange={e => setPhone(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSendPhoneOTP()} placeholder="77 234 5678" type="tel" inputMode="numeric" autoComplete="tel" style={{ flex: 1, padding: "12px 14px", border: "1px solid var(--line)", borderRadius: "0 8px 8px 0", fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
                </div>
              </div>
              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8, marginBottom: 16 }}>{error}</div>}
              <button onClick={handleSendPhoneOTP} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", marginBottom: 12 }}>
                {loading ? "Sending..." : "Send verification code"}
              </button>
              <div style={{ textAlign: "center", fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>We will email your code to the address on your account. Check your spam folder if it does not arrive within a minute.</div>
            </>
          )}

          {isOtpStep && (
            <>
              {devOtp && (
                <div style={{ background: "rgba(200,150,62,0.1)", border: "1px solid rgba(200,150,62,0.3)", borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: "var(--gold-deep)", textAlign: "center" }}>
                  Sandbox code: <strong>{devOtp}</strong>
                </div>
              )}
              <div style={{ marginBottom: 20 }}>
                <label htmlFor="login-otp" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>6-digit code</label>
                <input id="login-otp" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").substring(0, 6))} onKeyDown={e => e.key === "Enter" && handleVerifyOTP()} placeholder="000000" maxLength={6} type="text" inputMode="numeric" autoComplete="one-time-code" style={{ width: "100%", padding: "16px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 32, fontFamily: "Fraunces, serif", fontWeight: 600, textAlign: "center", letterSpacing: 12, background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
              </div>
              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8, marginBottom: 16 }}>{error}</div>}
              <button onClick={handleVerifyOTP} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", marginBottom: 16 }}>
                {loading ? "Verifying..." : "Verify and sign in"}
              </button>
              <div style={{ textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
                Did not receive it?{" "}
                <button onClick={resendOTP} disabled={loading} style={{ color: "var(--gold-deep)", fontWeight: 600, background: "none", border: "none", cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontFamily: "inherit" }}>Resend</button>
                {" · "}
                <button onClick={() => { setStep("credentials"); setOtp(""); setError(""); setDevOtp(""); setPending(null) }} style={{ color: "var(--muted)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Go back</button>
              </div>
            </>
          )}
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "var(--muted)" }}>
          New to Zim Estates?{" "}
          <Link href="/auth/register" style={{ color: "var(--gold-deep)", fontWeight: 600 }}>Sign up free</Link>
        </div>
        <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>
          By signing in you agree to our{" "}
          <Link href="/terms" style={{ color: "var(--navy)" }}>Terms</Link>{" "}and{" "}
          <Link href="/privacy" style={{ color: "var(--navy)" }}>Privacy Policy</Link>.
        </div>
      </div>
    </div>
  )
}
